import {
  ClientFileMessage,
  ClientUserMessage,
  // @ts-expect-error Sendbird does not provide types
} from "@sendbird/uikit-react/types/types";

import { fetchApi } from "@/lib/api";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { User } from "./user";

/** Custom types the ops backend keys media messages by, derived from the file. */
export type MediaMessageCustomType =
  | "IMAGE_MESSAGE"
  | "VIDEO_MESSAGE"
  | "VOICE_MESSAGE"
  | "MEDIA";

export const getMessageTypeFromFile = (file: {
  type: string;
}): MediaMessageCustomType => {
  const mimeType = file.type.toLowerCase();

  if (mimeType.startsWith("audio/")) {
    return "VOICE_MESSAGE";
  }

  if (mimeType.startsWith("image/")) {
    return "IMAGE_MESSAGE";
  }

  if (mimeType.startsWith("video/")) {
    return "VIDEO_MESSAGE";
  }

  return "MEDIA";
};

export const saveSentMessage = async (
  messageSent: ClientUserMessage | ClientFileMessage,
  file: File | null,
  to_user: string,
) => {
  try {
    if (!messageSent) return;

    const message = file
      ? (messageSent as ClientFileMessage).plainUrl
      : (messageSent as ClientUserMessage).message;

    const customType = file
      ? getMessageTypeFromFile(file)
      : messageSent.customType || "TEXT";

    const serviceParams: Record<string, string | File> = {
      sdk_id: messageSent.messageId,
      to_user: to_user,
      message_type: "text",
      message: message,
      custom_type: customType,
      data: file ? file : "",
    };

    const formData = new FormData();

    for (const key in serviceParams) formData.append(key, serviceParams[key]);

    await fetchApi({
      url: "v1/messages",
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: formData,
    });

    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
};

type MessageMetadata = {
  transcription?: string;
} & Record<string, unknown>;

interface MessageResponse {
  message: {
    id: string;
    sdk_id: string;
    delivered_at: string;
    read_at: string;
    to_user: User;
    from_user: User;
    message_type: "ADMM" | "TEXT" | "FILE";
    message: string;
    attachment_url: string;
    custom_type: string;
    data: string;
    metadata: MessageMetadata;
    created_at: string;
    updated_at: string;
  };
  signedUrl: string;
}

const getMessageBySdkId = async (sdk_id: string) => {
  try {
    const response = await fetchApi<MessageResponse>({
      url: `v1/messages/${sdk_id}`,
      method: "GET",
    });

    return response.data || null;
  } catch (e) {
    console.log(e);
    return null;
  }
};

/**
 * Resolves a file message by sdk id, falling back to req_id when the server has
 * not yet indexed the sdk id (newly sent messages).
 */
export const useResolveFileMessage = (
  sdkId: string | null,
  reqId: string | null,
  { eager = true }: { eager?: boolean } = {},
) => {
  const { track } = useTrackEvent();
  const query = useQuery({
    queryKey: ["message", sdkId, reqId],
    queryFn: async () => {
      const bySdkId = sdkId ? await getMessageBySdkId(sdkId) : null;
      if (bySdkId) return bySdkId;
      return reqId ? await getMessageBySdkId(reqId) : null;
    },
    enabled: eager && !!sdkId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const requestTranscript = useCallback(async () => {
    const result = await query.refetch();
    if (!result.data?.message?.metadata?.transcription) {
      track(PostHogEvents.VOICE_NOTE_TRANSCRIPT_UNAVAILABLE);
    }
  }, [query.refetch, track]);

  return {
    signedUrl: query.data?.signedUrl ?? null,
    transcription: query.data?.message?.metadata?.transcription ?? null,
    isResolving: query.isLoading,
    isFetching: query.isFetching,
    requestTranscript,
  };
};

export const useHideMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      hidden,
    }: {
      messageId: string;
      hidden: boolean;
    }) =>
      fetchApi({
        url: `v2/admin/messages/${messageId}/hide`,
        method: "PATCH",
        data: { hidden },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["message"],
      });
      queryClient.invalidateQueries({
        queryKey: ["messages"],
      });
      queryClient.invalidateQueries({
        queryKey: ["messages-from-start"],
      });
    },
  });
};
